import React, { useEffect, useState } from "react";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { Badge, Button, Card, CardBody, CardHeader, Container, Table } from "@/Components/ui";
import { SITE_ID } from "@/assets/meta/meta";
import { authService } from "@/api/services/auth.service";
import toast from "react-hot-toast";

type AuthSession = {
  id: string;
  user_phone_num: string;
  user_name?: string | null;
  active_site_name?: string | null;
  client_source: "crm" | "pos" | string;
  device_name?: string;
  ip_address?: string;
  last_seen_at: string;
  refresh_expires_at: string;
  revoked_at?: string | null;
};

export default function AuthSessionManagementPage() {
  const [sessions, setSessions] = useState<AuthSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const loadSessions = async () => {
    if (!SITE_ID) return;
    setLoading(true);
    try {
      const response = await authService.getSiteAuthSessions(SITE_ID);
      setSessions(response.data?.results || []);
    } catch (error: any) {
      toast.error(error?.message || "접속 장치 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const revokeSession = async (session: AuthSession) => {
    if (!SITE_ID) return;
    setRevokingId(session.id);
    try {
      await authService.revokeSiteAuthSession(SITE_ID, session.id);
      toast.success("세션을 로그아웃했습니다.");
      await loadSessions();
    } catch (error: any) {
      toast.error(error?.message || "세션 로그아웃에 실패했습니다.");
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="접속 장치 관리" pageTitle="관리자 멤버" />
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h4 className="card-title mb-0">운영자 접속 장치</h4>
            <Button color="light" onClick={loadSessions} disabled={loading}>
              새로고침
            </Button>
          </CardHeader>
          <CardBody>
            <div className="table-responsive">
              <Table className="align-middle table-nowrap mb-0">
                <thead>
                  <tr>
                    <th>사용자</th>
                    <th>앱</th>
                    <th>장치</th>
                    <th>최근 접속</th>
                    <th>IP</th>
                    <th className="text-end">작업</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">불러오는 중...</td>
                    </tr>
                  ) : sessions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">접속 중인 운영 장치가 없습니다.</td>
                    </tr>
                  ) : (
                    sessions.map((session) => (
                      <tr key={session.id}>
                        <td>
                          <div className="fw-semibold">{session.user_name || session.user_phone_num}</div>
                          <div className="text-muted small">{session.user_phone_num}</div>
                        </td>
                        <td>
                          <Badge color={session.client_source === "pos" ? "success" : "primary"}>
                            {session.client_source.toUpperCase()}
                          </Badge>
                        </td>
                        <td>{session.device_name || "알 수 없는 장치"}</td>
                        <td>{new Date(session.last_seen_at).toLocaleString()}</td>
                        <td>{session.ip_address || "-"}</td>
                        <td className="text-end">
                          <Button
                            color="danger"
                            outline
                            size="sm"
                            onClick={() => revokeSession(session)}
                            disabled={revokingId === session.id}
                          >
                            {revokingId === session.id ? "처리 중" : "로그아웃"}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
